import { useEffect } from "react";
import type { ReactNode } from "react";
import { haptic } from "./telegram";
import type { Booga, StatBlock } from "./types";

// Display ranges (mirror src/config/balance.ts) — presentational only, for bar fill.
const STAT_RANGE: Record<keyof StatBlock, [number, number]> = {
  hp: [80, 200],
  atk: [10, 40],
  def: [5, 25],
  spd: [10, 40],
  crit: [0.05, 0.3],
};

const STAT_LABEL: Record<keyof StatBlock, string> = {
  hp: "HP",
  atk: "ATK",
  def: "DEF",
  spd: "SPD",
  crit: "CRIT",
};

export function fillPct(stat: keyof StatBlock, value: number): number {
  const [min, max] = STAT_RANGE[stat];
  return Math.round(((value - min) / (max - min)) * 100);
}

export type RarityTier = "legendary" | "epic" | "rare" | "common";

/** Coarse rank → tier bucket. Single source for badge color AND roster filtering. */
export function rarityTier(rank: number): RarityTier {
  if (rank <= 50) return "legendary";
  if (rank <= 250) return "epic";
  if (rank <= 800) return "rare";
  return "common";
}

function tierClass(rank: number): string {
  // Coarse tiers just for badge color; real rank is what matters.
  return `tier-${rarityTier(rank)}`;
}

// One themed color per class (the 7 archetypes from src/config/balance.ts). Single
// source of truth so the HP header and the roster pill read the same. Unknown/new
// classes fall back to the cave's rune cyan.
const ARCHETYPE_COLORS: Record<string, string> = {
  Nomad: "#e0b568",
  Hunter: "#6fcf5e",
  Warrior: "#ff7a5c",
  Shaman: "#3fe0c0",
  Caveman: "#d8c4a0",
  Zombie: "#c089ff",
  Cyborg: "#39ff14",
};

export function archetypeColor(archetype: string): string {
  return ARCHETYPE_COLORS[archetype] ?? "var(--rune)";
}

/**
 * Bottom-sheet overlay. Dims the screen and slides a panel up from the bottom.
 * Closes on backdrop tap, the ✕, or Escape. Renders nothing when closed.
 */
export function BottomSheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    haptic("light");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-head">
          <span>{title}</span>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner">
      <div className="spinner-rock" aria-hidden />
      {label ? <p>{label}</p> : null}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  full,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost" | "cyber" | "lava";
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <button
      className={`btn btn-${variant}${full ? " btn-full" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Wooden bone-tipped header plaque — the brand's title surface. */
export function Plaque({ children }: { children: ReactNode }) {
  return <div className="plaque">{children}</div>;
}

/** Gold BP currency coin (inline, scales to surrounding font-size). */
export function Coin() {
  return (
    <span className="coin" aria-label="Booga Points">
      BP
    </span>
  );
}

export function BoogaArt({
  src,
  alt,
  size = 96,
  fluid = false,
}: {
  src?: string;
  alt: string;
  size?: number;
  /** Render at a responsive ~half-screen size (roster hero art) instead of a fixed px square. */
  fluid?: boolean;
}) {
  return (
    <div
      className={`booga-art${fluid ? " fluid" : ""}`}
      style={fluid ? undefined : { width: size, height: size }}
    >
      {src ? (
        <img src={src} alt={alt} loading="lazy" />
      ) : (
        <span className="booga-art-fallback" aria-label={alt}>
          🦴
        </span>
      )}
    </div>
  );
}

export function RarityBadge({ rank }: { rank: number }) {
  return <span className={`rarity-badge ${tierClass(rank)}`}>#{rank}</span>;
}

export function ArchetypePill({ archetype }: { archetype: string }) {
  return (
    <span className="archetype-pill" style={{ color: archetypeColor(archetype) }}>
      {archetype}
    </span>
  );
}

export function StatBars({ stats }: { stats: StatBlock }) {
  const keys: (keyof StatBlock)[] = ["hp", "atk", "def", "spd", "crit"];
  return (
    <div className="stat-bars">
      {keys.map((k) => (
        <div key={k} className="stat-row">
          <span className="stat-label">{STAT_LABEL[k]}</span>
          <span className="stat-track">
            <span className="stat-fill" style={{ width: `${fillPct(k, stats[k])}%` }} />
          </span>
          <span className="stat-val">
            {k === "crit" ? `${Math.round(stats.crit * 100)}%` : Math.round(stats[k])}
          </span>
        </div>
      ))}
    </div>
  );
}

export function BoogaCard({
  booga,
  selected,
  badge,
  onClick,
}: {
  booga: Booga;
  selected?: boolean;
  badge?: string;
  onClick?: () => void;
}) {
  return (
    <button
      className={`booga-card${selected ? " selected" : ""}`}
      onClick={onClick}
      type="button"
    >
      {badge ? <span className="card-badge">{badge}</span> : null}
      <BoogaArt src={booga.mediaUrl} alt={booga.name} fluid />
      <div className="booga-card-head">
        <strong className="booga-name">{booga.name}</strong>
        <RarityBadge rank={booga.rank} />
      </div>
      <ArchetypePill archetype={booga.archetype} />
      <StatBars stats={booga.statBlock} />
    </button>
  );
}

export function HpBar({
  name,
  hp,
  max,
  archetype,
}: {
  name: string;
  hp: number;
  max: number;
  archetype: string;
}) {
  const pct = Math.max(0, Math.min(100, (hp / max) * 100));
  const low = pct <= 25;
  return (
    <div className="hp-block">
      <div className="hp-head">
        <span className="hp-name">{name}</span>
        <span className="hp-arch" style={{ color: archetypeColor(archetype) }}>
          {archetype}
        </span>
      </div>
      <div className="hp-track">
        <div className={`hp-fill${low ? " low" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="hp-num">{Math.max(0, Math.round(hp))} HP</span>
    </div>
  );
}
